import { detectionsApi } from '@/api/detections.api';
import type { DetectionModel, DetectionSortConfig } from '@/types/detection.types';
import { type DrillSide, ORIENTATION_ORDER } from '@/types/detection.types';
import { formatDateTime } from '@/utils/formatters';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePreviewDialog } from '@/components/dialogs/ImagePreviewDialog';
import { DetectionActions } from './DetectionActions';

interface DetectionsTableProps {
  detections: DetectionModel[];
  onDelete: (id: string) => void;
  projectId: string;
}

export const DetectionsTable: FC<DetectionsTableProps> = ({ detections, onDelete, projectId }) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState<DetectionSortConfig>({
    field: 'orientation',
    order: 'asc',
  });
  const [holesCounts, setHolesCounts] = useState<Record<string, number>>({});
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [previewErrors, setPreviewErrors] = useState<Record<string, boolean>>({});
  const [previewDetectionId, setPreviewDetectionId] = useState<string | null>(null);
  const previewUrlsRef = useRef<Record<string, string>>({});

  const sortedDetections = useMemo(() => {
    const sorted = [...detections];
    sorted.sort((a, b) => {
      if (sortConfig.field === 'holes_count') {
        // Sort by holes count
        const aCount = holesCounts[a.id] ?? 0;
        const bCount = holesCounts[b.id] ?? 0;
        return sortConfig.order === 'asc' ? aCount - bCount : bCount - aCount;
      }

      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (sortConfig.field === 'orientation') {
        const aIndex = ORIENTATION_ORDER.indexOf(aValue as DrillSide);
        const bIndex = ORIENTATION_ORDER.indexOf(bValue as DrillSide);

        // If orientation not in order list, put it at the end
        if (aIndex === -1 && bIndex === -1) {
          const comparison = String(aValue).localeCompare(String(bValue));
          return sortConfig.order === 'asc' ? comparison : -comparison;
        }
        if (aIndex === -1) return sortConfig.order === 'asc' ? 1 : -1;
        if (bIndex === -1) return sortConfig.order === 'asc' ? -1 : 1;

        return sortConfig.order === 'asc' ? aIndex - bIndex : bIndex - aIndex;
      }

      // For dates (created_at)
      const aDate = new Date(aValue).getTime();
      const bDate = new Date(bValue).getTime();
      return sortConfig.order === 'asc' ? aDate - bDate : bDate - aDate;
    });
    return sorted;
  }, [detections, sortConfig, holesCounts]);

  // Fetch holes count for each detection (with cache)
  useEffect(() => {
    const fetchHolesCounts = async () => {
      const cacheKey = `holes_counts_${projectId}`;
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes

      // Get current detection IDs
      const currentDetectionIds = new Set(detections.map((d) => d.id));

      // Try to load from cache first
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { counts, timestamp, detectionIds } = JSON.parse(cached) as {
            counts: Record<string, number>;
            timestamp: number;
            detectionIds: string[];
          };
          const now = Date.now();

          // Check if cache is still valid (time and detection IDs match)
          const cachedIds = new Set(detectionIds || []);
          const idsMatch =
            currentDetectionIds.size === cachedIds.size &&
            Array.from(currentDetectionIds).every((id) => cachedIds.has(id));

          if (now - timestamp < cacheExpiry && idsMatch) {
            // Cache is still valid - use cached counts, but only for current detections
            const validCounts: Record<string, number> = {};
            for (const id of currentDetectionIds) {
              if (counts[id] !== undefined) {
                validCounts[id] = counts[id];
              }
            }

            // Check if we have all counts cached
            const missingIds = Array.from(currentDetectionIds).filter((id) => !(id in validCounts));

            if (missingIds.length === 0) {
              // All counts are cached
              setHolesCounts(validCounts);
              return;
            }

            // Some counts are missing - fetch only missing ones
            const missingPromises = missingIds.map(async (id) => {
              try {
                const holesCount = await detectionsApi.getHolesCount(id);
                return { id, count: holesCount };
              } catch (_error) {
                return { id, count: 0 };
              }
            });

            const missingResults = await Promise.all(missingPromises);
            for (const result of missingResults) {
              validCounts[result.id] = result.count;
            }

            // Update cache with all counts
            try {
              localStorage.setItem(
                cacheKey,
                JSON.stringify({
                  counts: validCounts,
                  timestamp: Date.now(),
                  detectionIds: Array.from(currentDetectionIds),
                })
              );
            } catch (_error) {
              // localStorage might be full or disabled, continue anyway
            }

            setHolesCounts(validCounts);
            return;
          }
        }
      } catch (_error) {
        // Cache invalid or doesn't exist, continue to fetch
      }

      // Load all counts in parallel
      const countPromises = detections.map(async (detection) => {
        try {
          const holesCount = await detectionsApi.getHolesCount(detection.id);
          return { id: detection.id, count: holesCount };
        } catch (_error) {
          return { id: detection.id, count: 0 };
        }
      });

      const results = await Promise.all(countPromises);
      const counts: Record<string, number> = {};
      for (const result of results) {
        counts[result.id] = result.count;
      }

      // Save to cache
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            counts,
            timestamp: Date.now(),
            detectionIds: Array.from(currentDetectionIds),
          })
        );
      } catch (_error) {
        // localStorage might be full or disabled, continue anyway
      }

      setHolesCounts(counts);
    };

    if (detections.length > 0) {
      fetchHolesCounts();
    }
  }, [detections, projectId]);

  // Fetch preview images for each detection (with IndexedDB cache)
  useEffect(() => {
    const fetchPreviews = async () => {
      // Cleanup previous URLs before fetching new ones
      Object.values(previewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      previewUrlsRef.current = {};

      const { getCachedBlob, setCachedBlob } = await import('@/utils/blobCache');
      const urls: Record<string, string> = {};
      const errors: Record<string, boolean> = {};

      // Load all previews in parallel (with cache)
      const previewPromises = detections.map(async (detection) => {
        const cacheKey = `3d_${detection.id}`;

        // Try cache first
        let blob = await getCachedBlob(cacheKey);
        if (!blob) {
          // Not in cache, fetch from API
          try {
            blob = await detectionsApi.get3DPreview(detection.id);
            // Cache the blob
            await setCachedBlob(cacheKey, blob);
          } catch (error: unknown) {
            const axiosError = error as { response?: { status?: number } };
            // Mark as error only if it's not 404 (preview might not exist yet - that's OK)
            if (axiosError.response?.status && axiosError.response.status !== 404) {
              errors[detection.id] = true;
            }
            // For 404, we just don't set a URL, so it will show loading/empty state
            return;
          }
        }

        // Create blob URL from cached or fetched blob
        const objectUrl = URL.createObjectURL(blob);
        urls[detection.id] = objectUrl;
        previewUrlsRef.current[detection.id] = objectUrl;
      });

      await Promise.all(previewPromises);

      setPreviewUrls(urls);
      setPreviewErrors(errors);
    };

    if (detections.length > 0) {
      fetchPreviews();
    }

    // Cleanup object URLs on unmount or when detections change
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      previewUrlsRef.current = {};
    };
  }, [detections, projectId]);

  const handleSort = (field: DetectionSortConfig['field']) => {
    setSortConfig((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon: FC<{ field: DetectionSortConfig['field'] }> = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ArrowDown size={14} className="opacity-30" />;
    }
    return sortConfig.order === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  if (detections.length === 0) {
    return null;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4 font-medium text-sm text-gray-700">
              <div
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleSort('orientation')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('orientation');
                  }
                }}
                tabIndex={0}
                role="button"
              >
                <span>{t('projects.orientation')}</span>
                <SortIcon field="orientation" />
              </div>
            </th>
            <th className="text-left py-3 px-4 font-medium text-sm text-gray-700">{t('projects.preview')}</th>
            <th className="text-left py-3 px-4 font-medium text-sm text-gray-700">
              <div
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleSort('created_at')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('created_at');
                  }
                }}
                tabIndex={0}
                role="button"
              >
                <span>{t('projects.created')}</span>
                <SortIcon field="created_at" />
              </div>
            </th>
            <th className="text-left py-3 px-4 font-medium text-sm text-gray-700">
              <div
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleSort('holes_count')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('holes_count');
                  }
                }}
                tabIndex={0}
                role="button"
              >
                <span>{t('projects.holesCount')}</span>
                <SortIcon field="holes_count" />
              </div>
            </th>
            <th className="text-right py-3 px-4 font-medium text-sm text-gray-700">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {sortedDetections.map((detection) => (
            <tr key={detection.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 text-sm">
                <span className="font-mono font-semibold text-primary">{detection.orientation}</span>
              </td>
              <td
                className="py-3 px-4 cursor-pointer hover:bg-gray-50/50"
                title={t('projectDetail.view3DPreview') || 'View 3D preview'}
                role="button"
                tabIndex={0}
                onClick={() => previewUrls[detection.id] && setPreviewDetectionId(detection.id)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && previewUrls[detection.id]) {
                    e.preventDefault();
                    setPreviewDetectionId(detection.id);
                  }
                }}
                aria-label={t('projectDetail.view3DPreview') || 'View 3D preview'}
              >
                {previewUrls[detection.id] ? (
                  <img
                    src={previewUrls[detection.id]}
                    alt={`Preview for ${detection.orientation}`}
                    className="w-16 h-16 object-contain pointer-events-none"
                    onError={() => {
                      setPreviewErrors((prev) => ({ ...prev, [detection.id]: true }));
                    }}
                  />
                ) : previewErrors[detection.id] ? (
                  <div className="w-16 h-16 flex items-center justify-center text-xs text-gray-400 pointer-events-none">
                    Error
                  </div>
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center text-xs text-gray-400 pointer-events-none">
                    —
                  </div>
                )}
              </td>
              <td className="py-3 px-4 text-sm text-gray-600">{formatDateTime(detection.created_at)}</td>
              <td className="py-3 px-4 text-sm text-gray-900">
                {holesCounts[detection.id] !== undefined ? holesCounts[detection.id] : '...'}
              </td>
              <td className="py-3 px-4 text-right">
                <div className="flex justify-end">
                  <DetectionActions detection={detection} onDelete={onDelete} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 3D Preview dialog – opened by clicking the preview image */}
      {previewDetectionId && (
        <ImagePreviewDialog
          visible={!!previewDetectionId}
          title={`3D Preview: ${sortedDetections.find((d) => d.id === previewDetectionId)?.orientation ?? previewDetectionId}`}
          imageUrl={previewUrls[previewDetectionId] ?? null}
          error={!!previewErrors[previewDetectionId]}
          onClose={() => setPreviewDetectionId(null)}
        />
      )}
    </div>
  );
};
